import { api } from "@/api";
import { QueryProps } from "@/types/Api";

export interface GetFullMatchesParams {
  status?: string;
  /** Multi-status variant of `status` (serialised as `statuses[]=`); the API
   *  rejects a request that carries both. */
  statuses?: string[];
  sportId?: number;
  leagueId?: number;
  countryId?: number;
  providerId?: number;
  syncLocked?: boolean;
  syncFlagged?: boolean;
  startDate?: string;
  endDate?: string;
  search?: string;
  /** Display language of the participant names (Turkish names when "tr"). */
  lang?: string;
}

export interface MatchParticipantDto {
  id: number;
  participant: string;
  participantId: number;
  name: string;
  score: number;
  yellowCards: number;
  redCards: number;
  corners: number;
}

export interface MatchEventDto {
  id: number;
  providerEventId: string;
  type: string;
  minute: number;
  seconds: number;
  period: number;
  participant: string;
  cardType: string | null;
  side: string | null;
  /** Goal method — "penalty" for a converted penalty, null otherwise. */
  method: string | null;
  /** Already sent by the API; declared so consumers can filter on it. */
  confirmationStatus: string;
}

export interface FullMatchDto {
  id: string;
  providerMatchId: number;
  category: string;
  sportId: number;
  leagueId: number;
  countryId: number;
  name: string;
  status: string;
  currentPeriod: number;
  startDate: string;
  coverageLevel: number;
  providerId: number;
  flag: string | null;
  leagueSpecialIcon: string | null;
  leaguePopular: boolean;
  countryFlag: string | null;
  /** Set when the match was stopped after kickoff; null for a pre-kickoff
   *  postponement/cancellation. */
  interruptedAt: string | null;
  /** Proven match clock at the stoppage; null = unknown or not interrupted. */
  interruptedMinute: number | null;
  syncLockedAt: string | null;
  /** Who placed the current sync lock; null when unlocked or the lock predates
   *  the audit trail. */
  syncLockedByUsername?: string | null;
  syncFlaggedAt: string | null;
  participants: MatchParticipantDto[];
  periods: any[];
  events: MatchEventDto[];
  sources: any[];
  /** Running clock of a LIVE match from the live-status cache; null for every
   *  non-live row and for a live row the cache could not answer. */
  liveClock: MatchLiveClockDto | null;
  /** How many coupons reference this match (trash excluded). Sent to
   *  Admin/SubAdmin only; null for every other viewer. */
  couponCount: number | null;
}

export interface MatchLiveClockDto {
  elapsedMinutes: number | null;
  state: "on-going" | "break" | null;
  // The period in play (football: 1 / 2 halves, 3 / 4 extra time).
  period?: number | null;
  // Added time the referee announced (the board's "+8"), when published.
  addedMinutes?: number | null;
}

export const getFullMatches = async (
  props: QueryProps<GetFullMatchesParams>
): Promise<FullMatchDto[]> => {
  const res = await api.client.get(`/matches`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};

export interface FullMatchesPaginationMeta {
  totalItems: number;
  itemCount: number;
  currentPage: number;
  itemsPerPage: number;
  totalPages: number;
}

export type FullMatchesOrder = "recent" | "popularLeaguesFirst";

export interface FullMatchesPaginatedParams extends GetFullMatchesParams {
  page?: number;
  pageSize?: number;
  /** Server-side page order; `recent` when omitted. */
  orderBy?: FullMatchesOrder;
}

export interface PaginatedFullMatches {
  meta: FullMatchesPaginationMeta;
  items: FullMatchDto[];
}

// Server-side paginated matches (admin matches-list) — only one page is fetched
// and all filtering/search happens on the server, so the page stays light no
// matter how many matches exist.
export const getFullMatchesPaginated = async (
  props: QueryProps<FullMatchesPaginatedParams>
): Promise<PaginatedFullMatches> => {
  const res = await api.client.get(`/matches/paginated`, {
    signal: props.signal,
    params: props.params,
  });
  return res.data;
};
